import {
  formatBorgValue,
  formatStsReferenceNumber,
  formatStsRepetitionCount,
  formatStsRestInterval,
  getSTSInterpretationText,
  getSTSSeverityLabel,
  getSTSSeverityTone,
  getStsReferenceUnit,
  getStsReferenceValidationNote,
  STS_REFERENCE_CHAIR_HEIGHT_CM,
  summarizeStsResult,
  type StsRepetitionAttemptPayload,
  type StsTestKey,
} from '../sts/stsDomain';
import AssessmentHistoryComparisonCard, {
  type AssessmentHistoryComparisonEntry,
} from '../components/AssessmentHistoryComparisonCard';

export { getSTSSeverityLabel, getSTSSeverityTone };

interface STSResultRendererProps {
  testKey: StsTestKey;
  payload: unknown;
  assessmentDate?: string | null;
  comparisonHistory?: AssessmentHistoryComparisonEntry[];
}

const AttemptsLine = ({
  values,
  lowerIsBetter,
  detail,
}: {
  values: number[];
  lowerIsBetter: boolean;
  detail?: string | null;
}) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Tentativas válidas</div>
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-700">
      <span>
        {values.length
          ? values
              .map((value) => (lowerIsBetter ? `${value.toFixed(2)} segundos` : formatStsRepetitionCount(value)))
              .join(' · ')
          : 'Nenhuma tentativa registrada'}
      </span>
      {detail ? <span className="font-medium text-slate-500">{detail}</span> : null}
    </div>
  </div>
);

const AttemptCard = ({
  label,
  attempt,
  highlight = false,
}: {
  label: string;
  attempt: StsRepetitionAttemptPayload;
  highlight?: boolean;
}) => (
  <div className={`rounded-[24px] border bg-white p-5 shadow-sm ${highlight ? 'border-primary-200' : 'border-slate-100'}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
        <div className="mt-2 text-3xl font-black text-slate-900">{formatStsRepetitionCount(attempt.reps)}</div>
      </div>
      <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700">
        Cadeira {attempt.chairHeightCm != null ? `${attempt.chairHeightCm} cm` : '—'}
      </div>
    </div>

    <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
            <th className="px-4 py-3">Variável</th>
            <th className="px-4 py-3">Pré</th>
            <th className="px-4 py-3">Pós</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
          <tr>
            <td className="px-4 py-3 font-medium text-slate-900">Borg dispneia</td>
            <td className="px-4 py-3">{formatBorgValue(attempt.borgDyspneaPre)}</td>
            <td className="px-4 py-3">{formatBorgValue(attempt.borgDyspneaPost)}</td>
          </tr>
          <tr>
            <td className="px-4 py-3 font-medium text-slate-900">Borg fadiga</td>
            <td className="px-4 py-3">{formatBorgValue(attempt.borgLegPre)}</td>
            <td className="px-4 py-3">{formatBorgValue(attempt.borgLegPost)}</td>
          </tr>
          <tr>
            <td className="px-4 py-3 font-medium text-slate-900">SpO2</td>
            <td className="px-4 py-3">{attempt.spo2Pre != null ? `${attempt.spo2Pre}%` : '—'}</td>
            <td className="px-4 py-3">{attempt.spo2Post != null ? `${attempt.spo2Post}%` : '—'}</td>
          </tr>
          <tr>
            <td className="px-4 py-3 font-medium text-slate-900">FC</td>
            <td className="px-4 py-3">{attempt.hrPre != null ? `${attempt.hrPre} bpm` : '—'}</td>
            <td className="px-4 py-3">{attempt.hrPost != null ? `${attempt.hrPost} bpm` : '—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

export default function STSResultRenderer({
  testKey,
  payload,
  assessmentDate = null,
  comparisonHistory = [],
}: STSResultRendererProps) {
  const summary = summarizeStsResult(testKey, payload);
  const severityLabel = getSTSSeverityLabel(testKey, payload);
  const interpretation = getSTSInterpretationText(testKey, payload);
  const bestAttemptIndex =
    summary.best == null ? null : summary.validAttempts.findIndex((value) => value === summary.best) + 1;
  const attemptMeta = summary.completed
    ? summary.attemptCount > 1
      ? `Melhor de ${summary.attemptCount} tentativas${bestAttemptIndex ? ` • ${bestAttemptIndex}ª tentativa` : ''}`
      : '1 tentativa registrada'
    : summary.workflowStatus === 'awaiting_second_attempt'
      ? 'Rascunho aguardando segunda tentativa'
      : 'Sem protocolo concluído';
  const referenceComparison = summary.referenceComparison;
  const referenceValidationNote = referenceComparison
    ? getStsReferenceValidationNote(referenceComparison)
    : '';

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">{summary.title}</p>
          <div className="text-6xl font-black leading-none" style={{ color: summary.scoreColor }}>
            {summary.best != null ? (summary.lowerIsBetter ? summary.best.toFixed(2) : Math.round(summary.best).toString()) : '—'}
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-700">
            {summary.best != null ? summary.measurementUnit : 'sem valor valido'}
          </div>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold" style={{ color: summary.scoreColor }}>
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            {severityLabel}
          </div>
          <div className="mt-3 text-xs font-medium text-slate-500">{attemptMeta}</div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>

          {referenceComparison ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="grid divide-y divide-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <div className="px-4 py-3">
                  <div className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">Previsto</div>
                  <div className="mt-1 text-sm font-bold text-slate-900">
                    {getStsReferenceUnit(testKey, referenceComparison.predictedValue)}
                  </div>
                </div>
                <div className="px-4 py-3">
                  <div className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">% do previsto</div>
                  <div className="mt-1 text-sm font-bold text-slate-900">
                    {formatStsReferenceNumber(referenceComparison.percentPredicted, 0)}%
                  </div>
                </div>
                <div className="px-4 py-3">
                  <div className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">
                    Limite {referenceComparison.normalLimitDirection === 'upper' ? 'superior' : 'inferior'}
                  </div>
                  <div className="mt-1 text-sm font-bold text-slate-900">
                    {getStsReferenceUnit(testKey, referenceComparison.normalLimitValue)}
                  </div>
                </div>
              </div>
              <details className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600">
                <summary className="cursor-pointer font-semibold text-slate-700">Cálculo da referência individual</summary>
                <div className="mt-2 space-y-1 leading-relaxed">
                  <p>Equação: {referenceComparison.equationLabel}.</p>
                  {testKey === 'STS_1MIN' ? <p>Sexo codificado como 0 para mulher e 1 para homem.</p> : null}
                  <p>
                    Furlanetto et al., 2022. Faixa de validação: 20 a 80 anos e IMC de 18 a 40 kg/m²;
                    protocolo de referência com cadeira de {STS_REFERENCE_CHAIR_HEIGHT_CM} cm e sem apoio dos braços.
                  </p>
                  {referenceValidationNote ? <p className="font-semibold text-amber-700">{referenceValidationNote}</p> : null}
                </div>
              </details>
            </div>
          ) : null}

          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Altura da cadeira: <span className="font-semibold text-slate-900">{summary.chairHeightCm != null ? `${summary.chairHeightCm} cm` : '—'}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Intervalo entre tentativas:{' '}
              <span className="font-semibold text-slate-900">
                {summary.restIntervalSeconds != null ? formatStsRestInterval(summary.restIntervalSeconds) : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <AssessmentHistoryComparisonCard
        testKey={testKey}
        currentPayload={payload}
        currentAssessmentDate={assessmentDate}
        history={comparisonHistory}
      />

      {testKey === '5xSTS' ? (
        <AttemptsLine
          values={summary.validAttempts}
          lowerIsBetter={summary.lowerIsBetter}
          detail={summary.usedArms ? 'Uso dos braços registrado' : 'Sem uso dos braços'}
        />
      ) : null}

      {testKey !== '5xSTS' && (summary.attempt1 || summary.attempt2) ? (
        <div className="space-y-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Tentativas registradas</div>
          <div className="grid gap-6 xl:grid-cols-2">
            {summary.attempt1 ? (
              <AttemptCard
                label={`1ª tentativa${summary.best === summary.attempt1.reps ? ' • melhor marca' : ''}`}
                attempt={summary.attempt1}
                highlight={summary.best === summary.attempt1.reps}
              />
            ) : null}
            {summary.attempt2 ? (
              <AttemptCard
                label={`2ª tentativa${summary.best === summary.attempt2.reps ? ' • melhor marca' : ''}`}
                attempt={summary.attempt2}
                highlight={summary.best === summary.attempt2.reps}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
